import { useTranslation } from 'react-i18next';
import ArrowForward from '@mui/icons-material/ArrowForward';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DateCalendar } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';

import useDateRangePicker from './hooks';

import './date-range-picker.scss';

type Props = TextFieldProps & {
  name: string;
  maxRangeInDays?: number;
  disablePast?: boolean;
};

const DateRangePicker = ({ name, maxRangeInDays, disablePast, ...forwardProps }: Props): React.JSX.Element => {
  const { t } = useTranslation('dateRangePicker');
  const {
    startDate,
    endDate,
    temporaryStartDate,
    temporaryEndDate,
    maxEndDate,
    dateRangePickerIsOpen,
    openDateRangePicker,
    closeDateRangePicker,
    applyTemporaryStartDate,
    applyTemporaryEndDate,
    apply,
  } = useDateRangePicker(name, maxRangeInDays);

  return (
    <>
      <FormControl className="date-range-picker-input">
        <TextField
          {...forwardProps}
          autoComplete="off"
          id={name}
          variant="outlined"
          onKeyDown={(e) => e.preventDefault()}
          onClick={openDateRangePicker}
          value={startDate && endDate ? `${startDate?.format('DD-MM-YYYY')} / ${endDate?.format('DD-MM-YYYY')}` : ''}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarMonthOutlined fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </FormControl>
      <Dialog
        open={dateRangePickerIsOpen}
        onClose={closeDateRangePicker}
        slotProps={{
          paper: {
            className: 'date-range-picker-popup-paper',
          },
        }}
        maxWidth="xl"
      >
        <div className="date-range-picker-popup-container">
          <div className="date-range-picker-header-container">
            <Typography variant="h5">{t('startDate')}</Typography>
            <ArrowForward />
            <Typography variant="h5">{t('endDate')}</Typography>
          </div>
          <Divider />
          <div className="date-range-picker-container">
            <DateCalendar
              value={temporaryStartDate}
              onChange={(date: Moment | null) => applyTemporaryStartDate(date ?? moment())}
              defaultValue={moment()}
              disablePast={disablePast}
            />
            <DateCalendar
              value={temporaryEndDate}
              onChange={(date: Moment | null) => applyTemporaryEndDate(date ?? moment())}
              defaultValue={moment()}
              maxDate={maxEndDate}
              disablePast={disablePast}
            />
          </div>
          <div className="date-range-picker-actions-container">
            <Button className="button-center-text" variant="outlined" onClick={closeDateRangePicker}>
              {t('actions.cancelText')}
            </Button>
            <Button variant="contained" onClick={apply}>
              {t('actions.applyText')}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default DateRangePicker;
